import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TypeOrmFindManyOptions } from '@wilson/base';
import { ConfigOptions, ConfigService } from '@wilson/config';
import {
  CreateInvoiceDTO,
  CreateInvoicePdfPayload,
  Invoice,
  InvoiceDTO,
  InvoiceTotalsDTO,
  PreviewInvoiceDTO,
  UpdateInvoiceDTO,
  InvoicePreviewResponse,
} from '@wilson/invoicing/interfaces';
import { stringify } from 'qs';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

type WhereOption = {
  createdAt?: {
    gte?: string;
    lte?: string;
  };
};

export type InvoicesOptions = {
  where?: WhereOption;
  relations?: string[];
  pageNumber?: number;
  pageSize?: number;
  orderBy?: {
    startDatetime: 'asc' | 'desc';
  };
};

type PdfMetaInformation = {
  name: string;
  display_name: string;
  encoding: string;
  'content-type': string;
};

export type InvoiceNumberAvailability = {
  available: boolean;
};

export type PdfGeneratedResponse = {
  response: string;
  meta: PdfMetaInformation;
};

@Injectable({
  providedIn: 'root',
})
export class InvoicesGateway {
  protected readonly http = inject(HttpClient);
  protected readonly config = inject<ConfigOptions>(ConfigService);

  protected readonly path = 'invoices';

  generatePDF(pdfPayload: CreateInvoicePdfPayload) {
    return this.http.post<PdfGeneratedResponse>(
      `${this.config.host}/${this.path}/pdf`,
      pdfPayload,
    );
  }

  getInvoices(options?: InvoicesOptions): Observable<InvoiceDTO[]> {
    const params = stringify(options);
    return this.http.get<InvoiceDTO[]>(
      `${this.config.host}/${this.path}?${params}`,
    );
  }

  /**
   * Sums net, VAT and gross across the given invoices (WILP-12865).
   *
   * Ids outside the caller's scope are omitted rather than rejected, so the
   * totals can cover fewer invoices than were requested. The endpoint rejects
   * an empty array and more than 1000 ids.
   */
  getInvoicesSummary(invoiceIds: string[]): Observable<InvoiceTotalsDTO> {
    return this.http.post<InvoiceTotalsDTO>(
      `${this.config.host}/v2/${this.path}/summary`,
      { invoiceIds },
    );
  }

  /**
   * Whether the number is still free inside the caller's root org unit
   * (WILP-12929). Advisory: finalization re-checks inside its transaction, so a
   * lost race still surfaces as `INVOICE_NUMBER_DUPLICATE`. Numbers used in
   * external systems are outside WILSON's visibility.
   */
  checkInvoiceNumberAvailability(
    invoiceNumber: string,
    excludeInvoiceId?: string | null,
  ): Observable<InvoiceNumberAvailability> {
    const params = stringify({
      invoiceNumber,
      ...(excludeInvoiceId ? { excludeInvoiceId } : {}),
    });
    return this.http.get<InvoiceNumberAvailability>(
      `${this.config.host}/v2/${this.path}/number-availability?${params}`,
    );
  }

  createInvoice(invoicePayload: CreateInvoiceDTO) {
    return this.http.put<InvoiceDTO>(
      `${this.config.host}/${this.path}`,
      invoicePayload,
    );
  }

  updateInvoice(invoicePayload: UpdateInvoiceDTO) {
    return this.http.put<InvoiceDTO>(
      `${this.config.host}/${this.path}`,
      invoicePayload,
    );
  }

  /**
   * Cancels an invoice, optionally carrying a user-supplied number for the cancellation invoice.
   *
   * In Manual Mode the organisation owns its invoice number sequence, so the
   * caller passes the number the user reserved for the cancellation invoice and
   * the backend uses it verbatim. In Compliant Mode no number is passed and the
   * request body stays empty, leaving the backend to assign one as before.
   */
  cancelInvoice(
    invoiceId: string,
    invoiceNumber?: string,
  ): Observable<InvoiceDTO> {
    return this.http.post<InvoiceDTO>(
      `${this.config.host}/${this.path}/${invoiceId}/cancel`,
      invoiceNumber ? { invoiceNumber } : {},
    );
  }

  getInvoice(id: string, options?: TypeOrmFindManyOptions) {
    const params = stringify(options);
    return this.http
      .get<
        InvoiceDTO & { calculatedInvoiceItems?: InvoiceDTO['calculatedItems'] }
      >(`${this.config.host}/${this.path}/${id}?${params}`)
      .pipe(
        map(({ calculatedInvoiceItems, ...invoice }) => ({
          ...invoice,
          calculatedItems: invoice.calculatedItems ?? calculatedInvoiceItems,
        })),
      );
  }

  deleteInvoices(ids: string[]) {
    const params = stringify({
      ids: ids,
    });
    return this.http.delete(`${this.config.host}/${this.path}?${params}`);
  }

  triggerCiiXmlGeneration(
    invoiceId: string,
  ): Observable<{ presignedUrl: string; expiresAt: string }> {
    return this.http.post<{ presignedUrl: string; expiresAt: string }>(
      `${this.config.host}/${this.path}/${invoiceId}/cii-xml`,
      {},
    );
  }

  exportPdf(invoiceId: string) {
    return firstValueFrom(
      this.http.get(
        `${this.config.host}/${this.path}/${invoiceId}/pdf/zugferd`,
        {
          responseType: 'blob',
          observe: 'response',
        },
      ),
    );
  }

  downloadPdf(invoiceId: string): Observable<PdfGeneratedResponse> {
    return this.http.get<PdfGeneratedResponse>(
      `${this.config.host}/${this.path}/${invoiceId}/pdf`,
    );
  }

  exportDatev(invoiceId: string) {
    return firstValueFrom(
      this.http.get(
        `${this.config.host}/${this.path}/${invoiceId}/export/zip`,
        {
          responseType: 'blob',
          observe: 'response',
        },
      ),
    );
  }

  createLexOffice(invoiceId: string) {
    return this.http.post<Invoice>(
      `${this.config.host}/${this.path}/${invoiceId}/external`,
      {},
    );
  }

  exportLexOffice(invoiceId: string) {
    return this.http.get<Blob>(
      `${this.config.host}/${this.path}/${invoiceId}/external`,
      {},
    );
  }

  previewInvoice(
    payload: PreviewInvoiceDTO,
    frozen: boolean,
  ): Observable<InvoicePreviewResponse> {
    return this.http.post<InvoicePreviewResponse>(
      `${this.config.host}/${this.path}/preview?frozen=${frozen}`,
      payload,
    );
  }

  generatePDFWithLambda(
    pdfPayload: CreateInvoicePdfPayload,
  ): Observable<{ statusFileUrl: string }> {
    return this.http.post<{ statusFileUrl: string }>(
      `${this.config.host}/invoices/merged-pdf`,
      pdfPayload,
    );
  }
}
